/**
 * PublicLinkAccess Page
 *
 * Simplified public-facing content view for shared links.
 * Shows only public-appropriate information without internal metadata.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { publicLinksService } from '../services/public-links';
import {
  Container,
  Paper,
  Typography,
  CircularProgress,
  Box,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { StreamingMetadata } from '../types/viewer.types';
import { PageBuilderWrapper } from '../components/page-builder/PageBuilderWrapper';
import { ViewPreview } from '../components/views/ViewPreview';
import { PublicLinkAccessContext } from '../context/PublicLinkAccessContext';
import { PublicContentRender } from '../components/public/PublicContentRender';

export const PublicLinkAccess: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [content, setContent] = useState<any>(null);
  const [signedDownloadUrl, setSignedDownloadUrl] = useState<string | null>(null);
  const [fetchingUrl, setFetchingUrl] = useState(false);

  // Track if we've already accessed the link to prevent duplicate increments
  const hasAccessedRef = React.useRef(false);

  // sessionStorage key for the share-session token. Per-tab, per-link.
  // When present and still valid, the server skips the password prompt — so
  // returning to the share (e.g. browser back from a content tile) renders
  // immediately instead of re-challenging the visitor for their password.
  const shareTokenKey = linkId ? `publicLink:shareToken:${linkId}` : '';

  const accessLink = async (pwd?: string) => {
    if (!linkId || !token) {
      setError('Invalid link - missing link ID or token');
      setLoading(false);
      return;
    }

    // Prevent duplicate API calls that would increment access count multiple times
    // This happens due to React 18 Strict Mode double-rendering in development
    // or password retry scenarios
    if (hasAccessedRef.current && !pwd) {
      // If we've already accessed and this isn't a password retry, skip
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Read any previously-issued share-session token from sessionStorage.
      // It acts as proof of a prior password gate clearance.
      const persistedShareToken = shareTokenKey
        ? window.sessionStorage.getItem(shareTokenKey) || undefined
        : undefined;

      // Use service to access public link
      const data = await publicLinksService.accessPublicLink(linkId, token, pwd, persistedShareToken);

      // Mark that we've successfully accessed this link
      hasAccessedRef.current = true;

      // Persist the freshly-minted share-session token so back-navigation
      // (and reloads within the same tab) skip the password challenge.
      if (data?.shareToken && shareTokenKey) {
        try {
          window.sessionStorage.setItem(shareTokenKey, data.shareToken);
        } catch {
          // sessionStorage may be unavailable (e.g., privacy mode); fall
          // back to per-mount password prompts.
        }
      }

      // Set content data
      setContent(data);
      setRequiresPassword(false);
      setLoading(false);
    } catch (err: any) {
      setLoading(false);

      // Handle password errors
      if (err.code === 'INVALID_PASSWORD' || err.code === 'PASSWORD_REQUIRED' || err.message?.includes('password')) {
        // A stale/rejected share-session token is no longer useful; clear it
        // so the visitor is prompted normally instead of getting stuck.
        if (shareTokenKey) {
          try { window.sessionStorage.removeItem(shareTokenKey); } catch { /* ignore */ }
        }
        setRequiresPassword(true);
        setError(err.code === 'PASSWORD_REQUIRED' ? null : (err.message || 'Incorrect password'));
        return;
      }

      // Handle content status errors
      if (err.code === 'CONTENT_NOT_PUBLIC') {
        setError('This content is no longer publicly accessible');
        return;
      }

      // Handle link not found
      if (err.code === 'LINK_NOT_FOUND') {
        setError('This link is no longer available');
        return;
      }

      // Handle link expired
      if (err.code === 'LINK_EXPIRED') {
        setError('This link has expired');
        return;
      }

      // Handle max uses reached
      if (err.code === 'LINK_MAX_USES_REACHED') {
        setError('This link has reached its maximum usage limit');
        return;
      }

      // Generic error
      console.error('Error accessing public link:', err);
      setError(err.message || 'Failed to access public link');
    }
  };

  const fetchSignedUrl = async () => {
    if (!linkId || !token || fetchingUrl || signedDownloadUrl) return;

    setFetchingUrl(true);
    try {
      const data = await publicLinksService.getPublicLinkDownloadUrl(linkId, token, password);
      setSignedDownloadUrl(data.downloadUrl);
    } catch (err) {
      console.error('Error fetching signed URL:', err);
    } finally {
      setFetchingUrl(false);
    }
  };

  const handleDownload = async () => {
    if (!signedDownloadUrl) {
      await fetchSignedUrl();
      // Wait a moment for state to update, then retry
      setTimeout(() => handleDownload(), 100);
      return;
    }

    try {
      setFetchingUrl(true);

      // Use direct link download (bypasses CORS for presigned URLs)
      // This works because the presigned URL has all authentication in the query params
      const link = document.createElement('a');
      link.href = signedDownloadUrl;
      link.download = content?.title || 'download';
      link.target = '_blank'; // Open in new tab as fallback
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error('Error downloading file:', err);
      setError('Failed to download file. Please try again.');
    } finally {
      setFetchingUrl(false);
    }
  };

  useEffect(() => {
    accessLink();
  }, [linkId, token]);

  useEffect(() => {
    if (!content) return;

    // Views render via the in-app PageBuilderWrapper / ViewPreview components
    // using the payload (pageDoc, mdxContent) already in the accessPublicLink
    // response — no extra fetch needed.
    if (content.type === 'view') {
      return;
    }

    // Content (file) mode: fetch the signed download URL.
    fetchSignedUrl();
  }, [content, password]);

  // Helper to determine content type from MIME type and filename
  // This matches the logic in UniversalFileViewer's getSimpleFileType for consistency
  const getContentTypeFromMime = (mimeType: string, fileName?: string): StreamingMetadata['contentType'] => {
    if (!mimeType) return 'unknown';

    // Check filename extension first for more accurate detection
    if (fileName) {
      const ext = fileName.toLowerCase().split('.').pop();

      // Code files (including proto files)
      const codeExtensions = [
        'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'h', 'cs', 'go', 'rs',
        'rb', 'php', 'swift', 'kt', 'scala', 'r', 'lua', 'sh', 'bash', 'ps1',
        'dockerfile', 'makefile', 'yml', 'yaml', 'json', 'xml', 'sql', 'proto'
      ];
      if (codeExtensions.includes(ext || '')) return 'code';

      // HTML
      if (ext === 'html' || ext === 'htm') return 'html';

      // Markdown
      if (ext === 'md' || ext === 'markdown') return 'markdown';
    }

    // Check mimeType
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType === 'application/pdf') return 'document';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';

    // Check for HTML and Markdown via mimeType
    if (mimeType === 'text/html') return 'html';
    if (mimeType === 'text/markdown' || mimeType === 'text/x-markdown') return 'markdown';

    // Check for code file MIME types
    if (mimeType === 'application/javascript' || mimeType === 'application/x-javascript') return 'code';
    if (mimeType === 'application/json') return 'code';
    if (mimeType === 'application/xml' || mimeType === 'text/xml') return 'code';
    if (mimeType.startsWith('text/x-')) return 'code';

    // General text files
    if (mimeType.startsWith('text/')) return 'text';

    // Office documents
    if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'document';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'document';
    if (mimeType.includes('opendocument')) return 'document';

    // For unrecognized types, return 'unknown' and let UniversalFileViewer handle it
    return 'unknown';
  };

  // Build StreamingMetadata from public link content data
  const streamingMetadata = useMemo((): StreamingMetadata | undefined => {
    if (!content || !signedDownloadUrl) return undefined;

    const mimeType = content.mimeType || content.mime_type || 'application/octet-stream';
    const fileName = content.fileName || content.title || 'download';
    const contentType = getContentTypeFromMime(mimeType, fileName);

    // Only enable conversion for files that can actually be converted (documents)
    const canConvert = contentType === 'document' && mimeType !== 'application/pdf';

    return {
      contentId: content.id,
      fileName,
      mimeType,
      fileSize: content.fileSize || content.file_size || 0,
      contentType,
      canStream: true, // Enable streaming for proper viewer display
      canConvert, // Only Office documents can be converted
      availableFormats: [],
      presignedUrl: signedDownloadUrl, // Use the signed URL we fetched
      downloadStrategy: 'stream', // Use stream strategy to prevent auto-download
      downloadMethod: 'single',
      supportsRange: true, // Enable range requests for better viewing
      isLargeFile: (content.fileSize || content.file_size || 0) > 100 * 1024 * 1024, // > 100MB
    };
  }, [content, signedDownloadUrl]);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length >= 6) {
      accessLink(password);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Loading shared content...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Password required state
  if (requiresPassword && !content) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LockIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Password Protected
            </Typography>
            <Typography variant="body1" color="text.secondary">
              This shared content requires a password to access
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handlePasswordSubmit}>
            <TextField
              fullWidth
              type="password"
              label="Enter Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password (minimum 6 characters)"
              sx={{ mb: 3 }}
              autoFocus
              helperText="Enter the password provided to you"
            />
            <Button
              fullWidth
              variant="contained"
              size="large"
              type="submit"
              disabled={password.length < 6}
            >
              Access Content
            </Button>
          </form>
        </Paper>
      </Container>
    );
  }

  // Error state
  if (error && !content) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="error">
            <Typography variant="h6" gutterBottom>Unable to Access Link</Typography>
            <Typography variant="body1">{error}</Typography>
          </Alert>
        </Paper>
      </Container>
    );
  }

  // Content display state
  if (content) {
    const archiveMimes = new Set([
      'application/zip',
      'application/x-zip-compressed',
      'application/x-tar',
      'application/gzip',
      'application/x-gzip',
    ]);
    const contentMime = (content.mimeType || content.mime_type || '').toLowerCase();
    const contentFileName = (content.fileName || content.title || '').toLowerCase();
    const isArchive = archiveMimes.has(contentMime) || /\.(zip|tar|tar\.gz|tgz)$/.test(contentFileName);
    const archiveManifest = content.archive_manifest ?? content.archiveManifest ?? null;

    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        {/* Tier One Content Branding Header */}
        <Box
          component={Link}
          to="/"
          sx={{
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            textDecoration: 'none',
            color: 'inherit',
            '&:hover': {
              opacity: 0.8
            }
          }}
        >
          <Box
            component="img"
            src="/apple-touch-icon.png"
            alt="Tier One Content"
            sx={{ width: 40, height: 40 }}
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: 'text.primary'
            }}
          >
            Tier One Content
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ overflow: 'hidden' }}>
          {content.type === 'view' ? (
            // ── View render mode ─────────────────────────────────────────────────
            // Mount the same renderer components the team-side /views/:id page
            // uses, fed by the payload (pageDoc / mdxContent) included in the
            // accessPublicLink response. Anchor clicks inside the body are
            // intercepted because the team-side anchors target /content/:id;
            // C3 will replace this with navigation to /p/content/:id?via=:viewLinkId.
            <Box sx={{ width: '100%', minHeight: 400 }}>
              <Box
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  const anchor = target.closest('a');
                  if (anchor) {
                    // Allow our own attribution footer link (target=_blank) to pass through
                    if (anchor.getAttribute('data-tier-one-attribution') === 'true') {
                      return;
                    }
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
              >
                {content.pageDoc ? (
                  <PublicLinkAccessContext.Provider
                    value={{
                      linkId: linkId!,
                      token: token!,
                      password: password || undefined,
                      // Forward the share-session token returned by accessPublicLink
                      // so embedded blocks can authenticate password-protected shares
                      // even on a back-nav round trip where React `password` state is empty.
                      shareToken: (content as any)?.shareToken || undefined,
                    }}
                  >
                    <PageBuilderWrapper
                      mode="view"
                      initialPageDoc={content.pageDoc}
                      teamId={content.teamId || content.team_id || ''}
                      organizationId={content.organizationId || ''}
                    />
                  </PublicLinkAccessContext.Provider>
                ) : content.mdxContent ? (
                  // No viewId → ViewPreview uses its client-side fallback render
                  // path (basic markdown). Sufficient for legacy MDX-only views.
                  <Box sx={{ p: 4 }}>
                    <ViewPreview
                      mdxContent={content.mdxContent}
                      organizationId={content.organizationId || ''}
                      teamId={content.teamId || content.team_id || ''}
                    />
                  </Box>
                ) : (
                  <Box sx={{ p: 4 }}>
                    <Alert severity="warning">
                      This view has no renderable content.
                    </Alert>
                  </Box>
                )}
              </Box>

              {/* Footer: small Tier One Content attribution */}
              <Box
                sx={{
                  borderTop: 1,
                  borderColor: 'divider',
                  py: 2,
                  px: 4,
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Created with{' '}
                  <Box
                    component="a"
                    href="https://tieronecontent.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-tier-one-attribution="true"
                    sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Tier One Content
                  </Box>
                </Typography>
              </Box>
            </Box>
          ) : (
            // ── Content (file) mode ───────────────────────────────────────────────
            <PublicContentRender
              content={content}
              streamingMetadata={streamingMetadata}
              signedDownloadUrl={signedDownloadUrl}
              publicLinkCredentials={{
                linkId: linkId!,
                token: token as string,
                password: password,
                shareToken: (content as any)?.shareToken,
                contentAccessToken: (content as any)?.contentAccessToken,
              }}
              fetchingUrl={fetchingUrl}
              onDownload={handleDownload}
              onError={(error) => setError(error.message)}
              isArchive={isArchive}
              archiveManifest={archiveManifest}
            />
          )}
        </Paper>
      </Container>
    );
  }

  return null;
};

export default PublicLinkAccess;
